/**
 * useSyncedTextReveal Hook
 *
 * Throttles AI text reveal to match audio playback position (karaoke effect),
 * with a **bursty / "arranques"** cadence inspired by the ChatGPT app.
 *
 * Instead of a constant-velocity char trickle, text is revealed in bursts of
 * roughly one word (1–2 syllables) at a time. We compute the ideal continuous
 * position from audio progress, then snap it DOWN to the nearest word boundary.
 * This produces natural holds (flat) followed by jumps (bursts) — the text
 * "pops" in small chunks rather than crawling at constant speed, while still
 * staying synced to the voice (each burst appears ~LEAD_MS before it's spoken).
 *
 *   targetChars = floor(text.length × (elapsedMs + leadMs) / totalMs)
 *   visibleChars = snapDownToWordBoundary(text, targetChars)
 *
 * The `leadMs` safety margin makes text appear slightly ahead of audio,
 * which feels more natural than strict sync.
 *
 * Pause-safe: Because the audio engine tracks accumulated played time
 * (not wall-clock subtraction), the elapsed value naturally freezes during
 * pause and resumes correctly — no special handling needed here.
 *
 * Monotonic reveal: The visible character count NEVER decreases. Both
 * `fullText` and `total` grow as the response streams in; without a
 * monotonic clamp, a new text delta (which grows `fullText.length`) arriving
 * before its matching audio chunk (which grows `total`) would make the
 * fraction drop and the text visibly recede — the "back-and-forth" artefact.
 * We track the high-water mark of visible characters and only ever advance.
 *
 * No full-text flash on isAudioPlaying toggle: The previous version showed
 * full text whenever `isAudioPlaying` was false. But `isAudioPlaying` is false
 * during prebuffering (first ~800ms before enough audio is buffered) and during
 * brief buffer underruns — causing the text to flash between full and throttled
 * (the "back-and-forth" bug) and to jump to full text near the end while audio
 * was still playing. Now, when `!isAudioPlaying` but audio HAS been received
 * (`total > 0`), we show the throttled text (frozen at current position) instead
 * of full text. Full text is only shown for text-only responses (`total === 0`).
 *
 * Fallbacks:
 * - If feature disabled → show full text immediately (current behavior)
 * - If no audio playing AND no audio received (total=0) → show full text (text-only)
 * - If audio finished (elapsed >= total) → show full text (naturally, via fraction=1)
 */

import { useState, useEffect, useRef } from 'react';

export interface UseSyncedTextRevealParams {
  /** The complete accumulated AI text (full transcript so far) */
  fullText: string;
  /** Returns accumulated audio playback time in ms (freezes during pause) */
  getAudioElapsedMs: () => number;
  /** Returns estimated total audio duration in ms for this response */
  getAudioTotalMs: () => number;
  /** Whether audio is currently playing */
  isAudioPlaying: boolean;
  /** Feature flag — when false, shows full text instantly (legacy behavior) */
  isEnabled: boolean;
}

/** Safety lead: text is revealed this many ms ahead of audio position */
const LEAD_MS = 200;

/**
 * Snap a target character index DOWN to the nearest preceding word boundary,
 * so text is revealed in word-sized bursts ("arranques") rather than a
 * constant char trickle.
 *
 * - If `target` lands in the middle of a word, we reveal up to the END of the
 *   previous word (so the burst is a whole word, not a partial one).
 * - If `target` lands on whitespace or at a word end, it stays put.
 * - We never return 0 once `target > 0`: the first burst always reveals at
 *   least the first word, so the very start of a response still "pops" in
 *   rather than crawling char-by-char.
 *
 * This is intentionally a FAKE approximation of syllable-level bursts: it
 * doesn't know which syllable the voice is actually on, but word-boundary
 * snapping produces the same "1–2 syllables at a time" feel the ChatGPT app
 * has, while staying synced to overall audio progress.
 *
 * @param text  The full text being revealed
 * @param target The ideal continuous char count (0..text.length)
 * @returns A char count snapped to a word boundary, in [0, text.length]
 */
function snapToWordBoundary(text: string, target: number): number {
  const len = text.length;
  if (target <= 0) return 0;
  if (target >= len) return len;

  // If the char AT target is whitespace, target is already on a boundary —
  // reveal through it (includes the trailing space with the previous word).
  if (/\s/.test(text[target])) {
    return target;
  }

  // Walk backwards from target to find the start of the current word, then
  // reveal up to (but not including) that word — i.e. the end of the previous
  // word plus its trailing space.
  let i = target;
  while (i > 0 && !/\s/.test(text[i - 1])) {
    i--;
  }

  // i is now the start index of the word that target falls inside.
  // Reveal up to i (end of previous word + its trailing whitespace).
  // But guarantee at least the first word is revealed once target > 0,
  // so the opening of a response bursts in rather than trickling.
  if (i === 0) {
    // target falls inside the FIRST word — reveal the whole first word.
    let end = target;
    while (end < len && !/\s/.test(text[end])) {
      end++;
    }
    return end;
  }

  return i;
}

export function useSyncedTextReveal({
  fullText,
  getAudioElapsedMs,
  getAudioTotalMs,
  isAudioPlaying,
  isEnabled,
}: UseSyncedTextRevealParams): string {
  const [visibleText, setVisibleText] = useState('');
  const rafIdRef = useRef<number | null>(null);
  // High-water mark of visible characters for THIS response. Prevents the
  // "back-and-forth" artefact: when a text delta arrives before its audio
  // chunk, the computed fraction can drop, but we never recede below this.
  const maxVisibleCharsRef = useRef(0);
  const prevFullTextRef = useRef(fullText);

  // Synchronous reset (during render, not in an effect): when fullText flips
  // to '' — the sentinel for "response finalized" — `messages` (the sibling
  // state driving the final bubble) has ALREADY been updated in this same
  // commit. If we waited for a useEffect to clear visibleText, the browser
  // could paint one frame where both the streaming bubble (stale, still
  // truthy visibleText) and the new final bubble are visible — the "flash"
  // of duplicated content. Clearing here guarantees visibleText is '' in the
  // very same render pass that fullText became ''.
  if (fullText !== prevFullTextRef.current) {
    prevFullTextRef.current = fullText;
    if (fullText === '') {
      setVisibleText('');
      maxVisibleCharsRef.current = 0;
    }
  }

  // Store getters in refs so they DON'T trigger effect re-runs. These are
  // inline arrow functions from useWebSocketSession that change identity on
  // every render; without refs, the effect would re-run on every render,
  // constantly cancelling and restarting the rAF loop.
  const getElapsedRef = useRef(getAudioElapsedMs);
  const getTotalRef = useRef(getAudioTotalMs);
  getElapsedRef.current = getAudioElapsedMs;
  getTotalRef.current = getAudioTotalMs;

  useEffect(() => {
    // Fallback: if feature disabled or no text — show full text (or empty)
    if (!isEnabled || !fullText) {
      setVisibleText(fullText);
      return;
    }

    const update = () => {
      const elapsed = getElapsedRef.current();
      const total = getTotalRef.current();

      if (total <= 0) {
        // No audio received yet.
        // Show NOTHING — not full text. Showing full text here and then throttling
        // to 1 char when audio arrives causes the "back-and-forth" recede at the
        // start of a response (text arrives before audio). The text will appear
        // proportionally once audio starts.
        // Exception: if audio is NOT playing AND not expected (no PLAYBACK_STARTED
        // yet), this could be a text-only response — but we can't know that yet.
        // The aiFinishing guard handles text-only responses (no audio → finalize
        // immediately → full text shown via the message, not the streaming bubble).
        setVisibleText('');
      } else {
        // Audio has been received — throttle proportionally, then snap to a word
        // boundary for the bursty "arranques" cadence.
        // This branch runs regardless of isAudioPlaying: during prebuffering
        // (isAudioPlaying=false, total>0), we show throttled text (NOT full text),
        // which fixes the back-and-forth flash. During underruns near the end,
        // we show throttled text (frozen at current position), which fixes the
        // premature full-text reveal.
        const fraction = Math.min(1, (elapsed + LEAD_MS) / total);
        const continuousChars = Math.max(1, Math.floor(fullText.length * fraction));
        // Snap down to a word boundary → bursts of ~1 word (1–2 syllables).
        const snappedChars = snapToWordBoundary(fullText, continuousChars);
        // Monotonic clamp: never recede.
        const visibleChars = Math.max(maxVisibleCharsRef.current, snappedChars);
        maxVisibleCharsRef.current = visibleChars;
        setVisibleText(fullText.substring(0, visibleChars));
      }

      // Continue the rAF loop ONLY while audio is actively playing.
      // When !isAudioPlaying, we stop the loop but DON'T reveal full text —
      // the last throttled value stays visible. The effect re-runs when
      // isAudioPlaying or fullText changes.
      if (isAudioPlaying) {
        rafIdRef.current = requestAnimationFrame(update);
      }
    };

    rafIdRef.current = requestAnimationFrame(update);

    return () => {
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
    };
  }, [fullText, isAudioPlaying, isEnabled]); // getters excluded — stored in refs

  return visibleText;
}
